/**
 * Demolition — the one action in the game that DESTROYS something the player paid
 * for, with no refund and no undo (the server spends the materials on build and
 * gives nothing back on unbuild).
 *
 * It used to happen on a single stray click in build mode: arm a structure from
 * the Build sheet, click anywhere near your house, house gone. That cost the owner
 * real buildings. So every demolition now goes through {@link confirmDemolish},
 * which names what dies and what it cost, defaults to Cancel, and closes on
 * Escape or a click outside. The wooden-sheet look is the same `sheet-card` panel
 * the rush popup uses; it is pinned BELOW the page header so it never covers the
 * wallet/nav chrome (a standing complaint about popups).
 */
import { el, toast } from '../../dom';
import { fieldApi, type Building } from '../../game-api';
import { richText } from '../../ui/icons';
import { playSfx } from '../../audio/audio';
import { BUILDINGS, guard, refreshBag, setFarm, type FarmCtx } from './context';
import { isUnderConstruction } from './construction';
import { buildCollision } from './scene';

/** Height of the fixed page chrome (header + nav), so a panel can clear it. */
function chromeBottom(): number {
  return Math.max(0, ...['.tb-header', 'nav'].map((sel) => {
    const elm = document.querySelector(sel) as HTMLElement | null;
    if (!elm || !elm.offsetParent) return 0;
    return Math.round(elm.getBoundingClientRect().bottom);
  }));
}

let overlay: HTMLElement | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;
let armTimer = 0;

export function closeDemolishConfirm(): void {
  overlay?.remove();
  overlay = null;
  if (armTimer) { window.clearTimeout(armTimer); armTimer = 0; }
  if (escHandler) { window.removeEventListener('keydown', escHandler, true); escHandler = null; }
}

/**
 * Ask before destroying. `onConfirm` runs only on an explicit click of the red
 * button — Cancel, Escape and a click on the backdrop all mean "keep it".
 *
 * The backdrop is INVISIBLE on purpose. It still covers the screen so a stray
 * click lands on "keep it" rather than on the farm behind, but it paints nothing:
 * dimming the whole game to ask a one-line question reads as the world being
 * switched off, and the owner asked for it gone. The card's own border and shadow
 * are what separate it from the map.
 */
export function confirmDemolish(b: Building, onConfirm: () => void): void {
  closeDemolishConfirm();
  playSfx('ui_open');
  const def = BUILDINGS[b.kind];
  const name = def?.name ?? b.kind;
  const unfinished = isUnderConstruction(b);

  const cost = [def?.wood ? `🪵 ${def.wood}` : '', def?.stone ? `🪨 ${def.stone}` : '']
    .filter(Boolean).join('  ');

  const lines: HTMLElement[] = [
    el('p', 'text-sm text-text-dim mb-2', {}, `Tear down your ${name}? This cannot be undone.`),
  ];
  if (cost) {
    lines.push(el('p', 'text-[11px] text-text-dim mb-2 inline-flex items-center justify-center gap-1', {},
      ...richText(`It cost ${cost} — nothing is refunded.`)));
  }
  if (unfinished) {
    // Worst case of all: a build the player may have paid SHROOMS to hurry along.
    // Say it in as many words before he clicks.
    lines.push(el('p', 'text-[11px] text-error mb-2', {},
      'It is still under construction — you lose all build progress and any Shrooms you spent rushing it.'));
  }

  // ARMING DELAY — the reason this dialog exists at all.
  // The overlay is created synchronously inside the pointertap handler, and the red
  // button lands right where the player just clicked on the map. A double-click on a
  // building (a natural gesture on a canvas that uses taps for everything) would put
  // click 2 straight onto "Demolish" and destroy the building in one gesture — the
  // exact accident the confirm is supposed to prevent. So the button ignores the
  // first ARM_MS, comfortably longer than a double-click interval, and says so.
  const ARM_MS = 400;
  const armedAt = Date.now() + ARM_MS;
  const danger = el('button',
    'w-full px-3 py-2 mb-2 rounded bg-error/20 border border-error text-error hover:bg-error/30 opacity-50 pointer-events-none',
    // Belt and braces: the class is removed on the timer, but a click that somehow
    // reaches us early (synthetic events, a stale frame) is still refused by time.
    { onClick: () => { if (Date.now() < armedAt) return; closeDemolishConfirm(); onConfirm(); } },
    `Demolish ${name}`);
  armTimer = window.setTimeout(() => {
    danger.classList.remove('opacity-50', 'pointer-events-none');
  }, ARM_MS);
  const cancel = el('button', 'w-full px-3 py-2 rounded border border-border text-text-dim hover:text-text',
    { onClick: () => closeDemolishConfirm() }, 'Keep it');

  const card = el('div', 'bg-surface border border-border rounded-lg p-5 w-[320px] max-w-[90vw] shadow-lift text-center sheet-card', {},
    el('h3', 'font-heading text-gold mb-1', {}, `Demolish ${name}?`),
    ...lines, danger, cancel);

  const top = chromeBottom() + 8;
  overlay = el('div', 'fixed inset-x-0 bottom-0 z-[70] flex items-start justify-center',
    { style: `top:${top}px`, onClick: (e) => { if (e.target === overlay) closeDemolishConfirm(); } },
    el('div', 'mt-[8vh]', {}, card));
  document.body.appendChild(overlay);
  // Cancel is the safe default, so it is what the keyboard lands on.
  cancel.focus();
  escHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();          // don't let the farm view read it as "close build mode" too
    closeDemolishConfirm();
  };
  window.addEventListener('keydown', escHandler, true);
}

/**
 * Actually remove the structure. Single funnel for every demolition path so the
 * post-conditions (bag + HUD refresh, collision rebuild, repaint) can never drift
 * apart between call sites — and so the hero is unstuck by the collision rebuild
 * if the thing he was standing inside was what got removed.
 */
export function demolish(c: FarmCtx, tx: number, ty: number, kind: string, repaint: (c: FarmCtx) => void): void {
  void guard('field.unbuild', async (actionId) => {
    setFarm(c, await fieldApi.unbuild(tx, ty, actionId));
    toast(`${BUILDINGS[kind]?.name ?? kind} removed`);
    await c.game.refreshHud();
    refreshBag(c);
    buildCollision(c);      // the tile is walkable again
    repaint(c);
  });
}
